/**
 * DeleteEventConfirmationModal Component
 *
 * Simple confirmation modal for deleting a tracked economic event.
 * Displays the event name and confirms the deletion action.
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

// 3. Types
import type { EconomicEventDraft } from '@/types/transient/EconomicEventDraft';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for DeleteEventConfirmationModal
 */
export interface DeleteEventConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The event being deleted (null when modal is closed) */
  event: EconomicEventDraft | null;
  /** Callback when modal closes (cancel) */
  onClose: () => void;
  /** Callback when delete is confirmed */
  onConfirm: () => void;
}

// =============================================================================
// Component
// =============================================================================

/**
 * DeleteEventConfirmationModal Component
 */
export const DeleteEventConfirmationModal: React.FC<DeleteEventConfirmationModalProps> = ({
  isOpen,
  event,
  onClose,
  onConfirm,
}) => {
  // Derive display name with fallback for unnamed events
  const eventName = event?.name || 'this event';

  // Guard: If no event, disable confirm button (defensive)
  const canConfirm = event !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      placement="center"
    >
      <ModalContent>
        <ModalHeader>Delete Event</ModalHeader>

        <ModalBody>
          <p className="text-sm text-text-secondary">
            Are you sure you want to delete <strong>{eventName}</strong>?
          </p>
          <p className="text-sm text-text-secondary mt-2">
            This action cannot be undone.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            Cancel
          </Button>
          <Button
            color="danger"
            isDisabled={!canConfirm}
            onPress={() => {
              if (canConfirm) {
                onConfirm();
                onClose();
              }
            }}
          >
            Delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteEventConfirmationModal;
